import { apiSlice } from "./apiSlice";
import {
  GameQuizEntity,
  GameQuizRemovalResult,
  GameQuizUsage,
  PaginatedGameQuizzesResponse,
} from "../../services/gameQuiz/gameQuiz.types";
import { GameQuizKind } from "../../services/gameQuizAttempts/gameQuizAttempts.types";

export interface GetGameQuizzesPaginatedArgs {
  page: number;
  pageSize: number;
  search?: string;
  /** Exact quiz id (SCRUM-975); combines with the other filters. */
  id?: number;
  published?: boolean;
  /** Quiz, videoquiz o lectura (SCRUM-1167). */
  kind?: GameQuizKind;
}

export interface GameQuizzesPage {
  data: GameQuizEntity[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export const gameQuizzesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGameQuizzesPaginated: builder.query<
      GameQuizzesPage,
      GetGameQuizzesPaginatedArgs
    >({
      query: ({ page, pageSize, search, id, published, kind }) => ({
        url: "/game-quizzes",
        method: "GET",
        params: {
          page,
          pageSize,
          ...(search && { search }),
          ...(id !== undefined && { id }),
          ...(published !== undefined && { published }),
          ...(kind && { kind }),
        },
      }),
      transformResponse: (
        response: PaginatedGameQuizzesResponse,
      ): GameQuizzesPage => ({
        data: response.data,
        pagination: {
          page: response.page,
          pageSize: response.pageSize,
          total: response.total,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "GameQuiz" as const,
                id,
              })),
              { type: "GameQuiz" as const, id: "LIST" },
            ]
          : [{ type: "GameQuiz" as const, id: "LIST" }],
    }),
    /**
     * What still points at a quiz. Fetched on demand right before deleting so the
     * admin sees the impact first; `keepUnusedDataFor: 0` because the answer is
     * only meaningful at that instant.
     */
    getGameQuizUsage: builder.query<GameQuizUsage, number>({
      query: (id) => ({
        url: `/game-quizzes/${id}/usage`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    deleteGameQuiz: builder.mutation<
      GameQuizRemovalResult,
      { id: number; confirm?: boolean }
    >({
      query: ({ id, confirm }) => ({
        url: `/game-quizzes/${id}`,
        method: "DELETE",
        // The backend refuses with 409 GAME_QUIZ_HAS_CONNECTIONS unless this is
        // set, so a caller that skipped the impact dialog cannot silently orphan
        // resources and homeworks.
        ...(confirm ? { params: { confirm: true } } : {}),
      }),
      invalidatesTags: [{ type: "GameQuiz", id: "LIST" }],
    }),
  }),
});

export const {
  useGetGameQuizzesPaginatedQuery,
  useLazyGetGameQuizUsageQuery,
  useDeleteGameQuizMutation,
} = gameQuizzesApi;
